<script lang="ts">
	import classNames from 'classnames';
	import { getAuthContext } from '#lib/websites/news/components/NewsHeader/AuthDialogs/utils/getAuthContext/getAuthContext.js';
	import { page } from '$app/state';
	import { HeartIcon } from '$global/assets/icons/HeartIcon';
	import { Txt } from '$global/components/Txt';
	import { SavedTagType } from '$gql/types-mutations';
	import type { NewsHomePageData } from '$routes/news/types';

	const dataCyPrefix = 'news-saved-count';

	interface Props {
		name: string;
	}

	interface TagDetails {
		id: string;
		type: SavedTagType;
		name: string;
		saved_tag_count: number;
	}

	let { name }: Props = $props();

	const {
		addUserTag,
		removeUserTag,
		user,
		usersPrefs,
		loginDialogStores,
		hasLoadedUserPrefs,
	} = getAuthContext();

	let tag = $derived.by((): TagDetails | null => {
		const profile = (page.data as NewsHomePageData).profile;
		const item = profile?.related_tag?.[0]?.item;

		if (!item?.id) {
			return null;
		}

		const type =
			item.__typename === 'HENI_News_Artists'
				? SavedTagType.Artist
				: SavedTagType.Geotag;

		return {
			id: item.id,
			type,
			name: item.name,
			saved_tag_count: item.saved_tag_count || 0,
		};
	});

	const getTagCount = () => {
		const baseCount = tag?.saved_tag_count || 0;
		return baseCount + 10;
	};

	let savedTagCount = $state(0);
	let isUserTag = $state(false);
	let isSavingTag = $state(false);

	$effect(() => {
		savedTagCount = getTagCount();
	});

	$effect(() => {
		isUserTag = !!$usersPrefs?.savedTags
			?.map((savedTag) => savedTag?.name)
			.includes(name);
	});

	let handleClickTag = $derived(async () => {
		if (!$user) {
			openLoginDialog();
			return;
		}

		if (!tag) {
			return;
		}

		isSavingTag = true;

		if (isUserTag) {
			removeUserTag({
				id: tag.id,
				type: tag.type,
				name: tag.name,
			}).then(() => {
				isSavingTag = false;
			});

			savedTagCount = savedTagCount - 1;
			isUserTag = false;
			return;
		}

		addUserTag({
			id: tag.id,
			type: tag.type,
			name: tag.name,
		}).then(() => {
			isSavingTag = false;
		});

		savedTagCount = savedTagCount + 1;
		isUserTag = true;
	});

	const openLoginDialog = () => {
		loginDialogStores.states.open.set(true);
	};
</script>

<button
	disabled={isSavingTag || ($user && !$usersPrefs.savedTags)}
	onclick={handleClickTag}
	class="relative shrink-0 transition-transform hover:scale-105"
	data-cy={`${dataCyPrefix}-heart-button`}
>
	<HeartIcon
		full={isUserTag}
		class={classNames('h-10 w-10', {})}
		color="red-500"
	/>

	{#if $hasLoadedUserPrefs && tag && savedTagCount}
		<Txt
			variant="label4"
			class={classNames(
				'absolute top-[-1px] left-0 flex h-full w-full items-center justify-center',
				{
					'text-[0.675rem]': savedTagCount < 100,
					'text-[0.5rem]': savedTagCount >= 100,
					'text-white': isUserTag,
					'text-red-500': !isUserTag,
				}
			)}
		>
			{savedTagCount}
		</Txt>
	{/if}
</button>
